import { flash } from "@xola/ui-kit";
import { AxiosResponse } from "axios";
import { Loader } from "common";
import { produce } from "immer";
import { findIndex, isUndefined } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import {
    AllowedPrivacy,
    CatalogItemObject,
    Experience,
    LineItemTemplate,
    PricingType,
    ProductCatalogItem,
    PurchaseRule,
    PurchaseRuleLink,
    UnitType,
} from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getCatalogItemsByUnitType } from "../../../models/Catalog";
import {
    generateDemographicPricingGroupFromPurchaseRules,
    generatePurchaseRulesFromDemographicPricingGroup,
} from "../../../models/DemographicPricingGroup";
import { getDefaultGroup, sanitizeExperiencePayload } from "../../../models/Experience";
import {
    getSchedulePurchaseRuleLinkPayload,
    getUpdatedPriceVariationPurchaseRules,
} from "../../../models/PriceVariation";
import { getPurchaseRulesFromPurchaseRuleLink } from "../../../models/PurchaseRuleLink";
import { PromptModal } from "../../Common/PromptModal";
import { ContentLayout } from "../../Layout/ContentLayout";
import { ExperiencePricingForm } from "./ExperiencePricingForm";
import {
    ExperiencePricingFormPayload,
    getInitialValues,
    getPurchaseRuleLinkPayload,
    shouldSaveCatalogItem,
} from "./ExperiencePricingFormHelper";

interface ExperiencePricingProps {
    experience: Experience;
    lineItemTemplates: LineItemTemplate[];
    purchaseRuleLink?: PurchaseRuleLink;
    schedulePurchaseRuleLink?: PurchaseRuleLink;
}

// Equipment lives in its own catalog-item collection; demographics and their secondaries share the
// product one.
const SAVEABLE_CATALOG_UNIT_TYPES = [
    [UnitType.DEMOGRAPHIC, CatalogItemObject.PRODUCT_CATALOG_ITEM],
    [UnitType.DEMOGRAPHIC_SECONDARY, CatalogItemObject.PRODUCT_CATALOG_ITEM],
    [UnitType.EQUIPMENT, CatalogItemObject.EQUIPMENT_CATALOG_ITEM],
] as const;

export const ExperiencePricing = ({
    experience,
    lineItemTemplates,
    purchaseRuleLink,
    schedulePurchaseRuleLink,
}: ExperiencePricingProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const purchaseRules = getPurchaseRulesFromPurchaseRuleLink(purchaseRuleLink);
    const publicPricingGroup = generateDemographicPricingGroupFromPurchaseRules(purchaseRules, AllowedPrivacy.PUBLIC);
    const privatePricingGroup = generateDemographicPricingGroupFromPurchaseRules(purchaseRules, AllowedPrivacy.PRIVATE);
    const [updatedExperience, setUpdatedExperience] = useState<ExperiencePricingFormPayload | null>(null);

    const schedulePurchaseRules = getPurchaseRulesFromPurchaseRuleLink(schedulePurchaseRuleLink).filter(
        (purchaseRule) => {
            return !purchaseRule.deletedAt;
        },
    );

    const { mutateAsync: updateExperience, isLoading: isSavingExperience } = useMutation(
        async (payload: Experience) => {
            return api.put(`/experiences/${payload.id}`, sanitizeExperiencePayload(payload));
        },
    );

    const { mutateAsync: saveCatalogItem, isLoading: isSavingCatalogItem } = useMutation(
        async (payload: ProductCatalogItem) => {
            const url = `/catalogItems`;
            return payload.id ? api.put(`${url}/${payload.id}`, payload) : api.post(url, payload);
        },
    );

    const { mutateAsync: deleteCatalogItem, isLoading: isDeletingCatalogItem } = useMutation(async (id: ID) => {
        return api.delete(`/catalogItems/${id}`);
    });

    const { mutateAsync: updatePurchaseRuleLink, isLoading: isSavingPurchaseRuleLink } = useMutation(
        async (payload: PurchaseRuleLink) => {
            if (payload?.id) {
                const { id } = payload;
                return api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${id}`, payload);
            }

            return api.post<PurchaseRuleLink>(`/purchaseRuleLinks`, payload);
        },
    );

    const { mutateAsync: savePurchaseRule, isLoading: isSavingPurchaseRule } = useMutation(
        async (payload: PurchaseRule) => {
            if (payload.id) {
                return api.put<PurchaseRule>(`/purchaseRules/${payload.id}`, payload);
            }

            return api.post<PurchaseRule>(`/purchaseRules`, payload);
        },
    );

    const { mutateAsync: deletePurchaseRule, isLoading: isDeletingPurchaseRule } = useMutation(async (id: ID) => {
        return api.delete(`/purchaseRules/${id}`);
    });

    const handleSubmit = async (values: ExperiencePricingFormPayload) => {
        /**
         * TODO: Implement Either all are succeded or none of them are strategy for saving. If api is failed
         *       we will need to delete created purchaseRules.
         */
        try {
            const catalogItemPromises: any = [];
            const currentAllowedPrivacies = experience.allowedPrivacies;
            for (const [unitType, catalogItemObject] of SAVEABLE_CATALOG_UNIT_TYPES) {
                for (const catalogItem of getCatalogItemsByUnitType(values.catalog.items, unitType)) {
                    const currentCatalogItem = produce(catalogItem, (draft) => {
                        draft.object = catalogItemObject;
                        draft.product = { id: experience.id };
                    });
                    if (shouldSaveCatalogItem(experience.catalog.items, currentCatalogItem)) {
                        catalogItemPromises.push(saveCatalogItem(currentCatalogItem));
                    }
                }
            }

            const payload = produce(values, (draft) => {
                for (const [unitType] of SAVEABLE_CATALOG_UNIT_TYPES) {
                    const currentCatalogItems = getCatalogItemsByUnitType(draft.catalog.items, unitType);
                    for (const item of getCatalogItemsByUnitType(experience.catalog.items, unitType)) {
                        const index = findIndex(currentCatalogItems, ["template.id", item.template?.id]);
                        if (index < 0 && item.id) {
                            catalogItemPromises.push(deleteCatalogItem(item.id));
                        }
                    }
                }

                const schedules = [];
                const selectedPrivateSchedules = draft.privatePricing.schedules;
                for (const schedule of draft.schedules) {
                    schedule.allowedPrivacies = draft.allowedPrivacies;
                    if (draft.allowedPrivacies.length > 1) {
                        /**
                         * If experience has both pricing and if the schedule is not in private schedules, then
                         * it will only have public as allowed Privacy
                         */
                        const isPrivateSchedule =
                            selectedPrivateSchedules.all ||
                            (schedule.id && selectedPrivateSchedules.items.includes(schedule.id));
                        if (!isPrivateSchedule) {
                            schedule.allowedPrivacies = [AllowedPrivacy.PUBLIC];
                        }
                    }

                    schedules.push(schedule);
                }

                draft.schedules = schedules;

                // checking the condition and if yes we should reset fields in "group" to default values except outingMax
                const { allowedPrivacies, privatePricing } = values;
                if (
                    allowedPrivacies.length === 1 &&
                    allowedPrivacies[0] === AllowedPrivacy.PRIVATE &&
                    privatePricing.pricingType === PricingType.PER_OUTING
                ) {
                    const outingMax = draft.group?.outingMax;
                    draft.group = getDefaultGroup();
                    if (!isUndefined(outingMax)) {
                        draft.group.outingMax = outingMax;
                    }
                }
            });

            // TODO: Make a Put call only if there is any change in catalogItem
            await Promise.all(catalogItemPromises);

            await updateExperience(payload)
                .then(async () => {
                    const { updates: purchaseRulesToBeSaved, deletions: purchaseRulesToBeDeleted } =
                        generatePurchaseRulesFromDemographicPricingGroup(
                            payload,
                            lineItemTemplates,
                            purchaseRuleLink,
                            values.publicPricing,
                            values.privatePricing,
                        );

                    const purchaseRulePromises: any = [];
                    for (const purchaseRule of purchaseRulesToBeDeleted) {
                        if (purchaseRule.id) {
                            await deletePurchaseRule(purchaseRule.id);
                        }
                    }

                    for (const purchaseRule of purchaseRulesToBeSaved) {
                        purchaseRulePromises.push(savePurchaseRule(purchaseRule));
                    }

                    return Promise.all(purchaseRulePromises);
                })
                .then(async (result) => {
                    const savedPurchaseRules = result.map((item) => (item as AxiosResponse<PurchaseRule>).data);
                    const { data: updatedPurchaseRuleLink } = await updatePurchaseRuleLink(
                        getPurchaseRuleLinkPayload(seller, experience, purchaseRuleLink, savedPurchaseRules),
                    );

                    // update schedule price variation purchase rules
                    if (schedulePurchaseRuleLink && schedulePurchaseRules.length > 0) {
                        const purchaseRulePromises: any = [];

                        if (isPriceTypeDifferent(values)) {
                            for (const rule of schedulePurchaseRules) {
                                if (rule.id) {
                                    deletePurchaseRule(rule.id);
                                    purchaseRulePromises.push(savePurchaseRule(rule));
                                }
                            }
                        } else {
                            const { updatedPurchaseRules, deletedPurchaseRules: purchaseRulesToBeDeleted } =
                                getUpdatedPriceVariationPurchaseRules(
                                    currentAllowedPrivacies,
                                    payload.allowedPrivacies,
                                    schedulePurchaseRuleLink,
                                    payload,
                                    lineItemTemplates,
                                );

                            for (const rule of updatedPurchaseRules) {
                                purchaseRulePromises.push(savePurchaseRule(rule));
                            }

                            for (const rule of purchaseRulesToBeDeleted) {
                                if (rule.id) {
                                    deletePurchaseRule(rule.id);
                                    purchaseRulePromises.push(savePurchaseRule(rule));
                                }
                            }
                        }

                        await Promise.all(purchaseRulePromises).then(async (result) => {
                            const savedPurchaseRules = result.map((item) => (item as AxiosResponse<PurchaseRule>).data);
                            const { data: updatedPurchaseRuleLink } = await updatePurchaseRuleLink(
                                getSchedulePurchaseRuleLinkPayload(
                                    seller.id,
                                    experience,
                                    schedulePurchaseRuleLink,
                                    savedPurchaseRules,
                                ),
                            );
                            invalidate("purchaseRuleLinks", updatedPurchaseRuleLink.id);
                        });
                    }

                    setTimeout(() => {
                        invalidate("products", experience.id);
                        invalidate("experiences", experience.id);
                        invalidate("purchaseRuleLinks", updatedPurchaseRuleLink.id);
                    }, 3000);

                    flash.success("Successfully updated experience");
                });
        } catch {
            flash.danger("Error updating experience");
            invalidate("products", experience.id);
            invalidate("experiences", experience.id);
        }
    };

    const isLoading =
        isSavingExperience ||
        isSavingPurchaseRule ||
        isDeletingPurchaseRule ||
        isSavingPurchaseRuleLink ||
        isSavingCatalogItem ||
        isDeletingCatalogItem;

    const isPriceTypeDifferent = (values: ExperiencePricingFormPayload) => {
        const updatedPublicPricingType = values.publicPricing.pricingType;
        const updatedPrivatePricingType = values.privatePricing.pricingType;

        return (
            updatedPublicPricingType !== publicPricingGroup.pricingType ||
            updatedPrivatePricingType !== privatePricingGroup.pricingType
        );
    };

    const shouldShowPriceTypeChangeWarning = (values: ExperiencePricingFormPayload) => {
        return schedulePurchaseRules.length > 0 && isPriceTypeDifferent(values);
    };

    const confirmSubmit = async (values: ExperiencePricingFormPayload) => {
        return shouldShowPriceTypeChangeWarning(values) ? setUpdatedExperience(values) : handleSubmit(values);
    };

    if (!publicPricingGroup || !privatePricingGroup) {
        return <Loader />;
    }

    return (
        <ContentLayout isWide isDisabled={isLoading}>
            <ExperiencePricingForm
                value={getInitialValues(experience, publicPricingGroup, privatePricingGroup)}
                isLoading={isLoading}
                experience={experience}
                lineItemTemplates={lineItemTemplates}
                publicPricingGroup={publicPricingGroup}
                privatePricingGroup={privatePricingGroup}
                onSubmit={confirmSubmit}
            />

            <PromptModal
                title="Warning: Price type change"
                isOpen={!!updatedExperience}
                dialogText={{ submit: "Continue", secondary: "Cancel" }}
                onClose={() => setUpdatedExperience(null)}
                onConfirm={() => {
                    if (updatedExperience) {
                        handleSubmit(updatedExperience);
                        setUpdatedExperience(null);
                    }
                }}
            >
                You’re changing the price type for this experience. Any existing schedule price variations will no
                longer apply after this change. Please click continue to confirm
            </PromptModal>
        </ContentLayout>
    );
};
